import classNames from 'classnames'
import camelCase from 'lodash/camelCase'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { Input } from '..'
import { InputProps } from '../Input'
import { Text } from 'components/Text'
import { Form } from './Form'
import { FormattedMessage } from 'react-intl'

type EmailVerificationInputProps<T extends FieldValues> = Pick<
  UseControllerProps<T>,
  'control' | 'rules' | 'name'
> & {
  className?: string
  email: string
  onInputChange?: (value: string) => void
  handleResend?: () => void
  codeError?: string
} & InputProps

export function EmailVerificationInput<T extends FieldValues>({
  name,
  email,
  control,
  className,
  rules,
  onInputChange,
  handleResend,
  codeError,
  ...props
}: EmailVerificationInputProps<T>) {
  const INPUT_STYLE: React.CSSProperties = {
    fontSize: 30,
    fontWeight: 600,
    height: 50,
    textAlign: 'center' as const,
    padding: 0,
    width: '100%',
  }
  const {
    field: { onChange, ...field },
    fieldState,
  } = useController({
    name,
    control,
    rules: {
      ...rules,
      maxLength: 6,
      minLength: 6,
      required: true,
      validate: {
        isValid: () => (codeError ? 'This code is invalid, sorry.' : true),
      },
    },
  })

  return (
    <div>
      <Text.Corpus className="line-height-larger margin-bottom-12 text-align-center shade-10">
        <FormattedMessage
          id="boarding.form.emailVerification.sentCode"
          defaultMessage="We've sent a 6-digit confirmation code to {email}. {br} Enter it below:"
          values={{
            br: <br />,
            email: <span className="font-weight-semibold">{email}</span>,
          }}
        />
      </Text.Corpus>
      <Input
        className={classNames('margin-vertical-24', className)}
        type="text"
        inputMode="numeric"
        placeholder="- - - - - -"
        error={fieldState.invalid}
        autoFocus
        data-test-id={camelCase(`${name} field`)}
        maxLength={6}
        onChange={(e) => {
          onChange(e)
          if (onInputChange) {
            onInputChange(e.target.value)
          }
        }}
        {...field}
        {...props}
        style={INPUT_STYLE}
      />
      <Form.Error
        message={fieldState.error?.message}
        render={(message) => (
          <FormattedMessage
            id="boarding.form.emailVerification.invalidCode"
            defaultMessage="{message}. Try to type it again"
            values={{
              message,
            }}
          />
        )}
      />
      <Text.Corpus className="line-height-larger shade-10 text-align-center margin-top-12 margin-bottom-12">
        <FormattedMessage
          id="boarding.form.emailVerification.keepWindowOpen"
          defaultMessage="Keep this window open while checking for your code. {br} <action>Click here</action> to receive a new code."
          values={{
            br: <br />,
            action: (text: React.ReactNode) => (
              <Text.Action inline onClick={handleResend} className="underline">
                {text}
              </Text.Action>
            ),
          }}
        />
      </Text.Corpus>
    </div>
  )
}
